import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { SafeArea, SystemBarsStyle, SystemBarsType } from '@capacitor-community/safe-area';

// Czy przelaczac ikony statusbara na biale, gdy scrim przyciemnia pasek?
//   true  → ikony robia sie biale na przyciemnieniu (czytelne na ciemnym tle)
//   false → ikony zostaja ciemne (bez zadnej zmiany natywnej — sam webowy scrim dimuje)
const FLIP_STATUS_BAR_ICONS = false;

const parseRgba = (str) => {
  const m = str.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const p = m[1].split(',').map((s) => parseFloat(s.trim()));
  return { r: p[0], g: p[1], b: p[2], a: p.length > 3 ? p[3] : 1 };
};

// Czy jest widoczny CIEMNY, POLPRZEZROCZYSTY scrim modala (≈pelnoekranowy)? To tylko BOOLEAN
// (obecnosc), a nie kolor per klatke — dzieki temu przelaczamy ikony RAZ, bez migotania.
const isDarkScrimVisible = () => {
  const els = document.querySelectorAll('[class*="inset-0"]');
  for (const el of els) {
    const s = getComputedStyle(el);
    if (s.position !== 'fixed' && s.position !== 'absolute') continue;
    const r = el.getBoundingClientRect();
    if (r.width < window.innerWidth * 0.9 || r.height < window.innerHeight * 0.7) continue;

    const c = parseRgba(s.backgroundColor);
    if (!c) continue;
    if (c.a >= 0.98) continue;                          // opaque panel — nie scrim
    if (c.r > 120 || c.g > 120 || c.b > 120) continue;  // musi byc ciemny

    const op = parseFloat(s.opacity);
    const a = c.a * (isNaN(op) ? 1 : op);
    if (a <= 0.12) continue;                            // realnie widoczny (nie na samym starcie fade)
    return true;
  }
  return false;
};

// Czy gorna krawedz jest zaslonieta nieprzezroczystym JASNYM panelem (np. pelnoekranowy bialy
// modal od gory)? Wtedy statusbar zostaje jasny → ciemne ikony.
const topCoveredByLightPanel = () => {
  const x = Math.floor(window.innerWidth / 2);
  let node = document.elementFromPoint(x, 2);
  while (node && node !== document.body) {
    const c = parseRgba(getComputedStyle(node).backgroundColor);
    if (c && c.a >= 0.98) return c.r > 200 && c.g > 200 && c.b > 200;
    if (c && c.a > 0.05) return false; // trafilismy w scrim → nie zasloniety
    node = node.parentElement;
  }
  return false;
};

// PLAN A: paski systemowe sa PRZEZROCZYSTE (customColorsForSystemBars:false), wiec pelnoekranowy
// webowy scrim (fixed inset-0 bg-black/xx) zaciemnia rowniez obszar ZA nimi — jedna animacja CSS,
// bez mostu w trakcie, bez migotania. Natywnie robimy TYLKO jedno: gdy statusbar jest przyciemniony
// scrimem, przelaczamy jego ikony na BIALE (zeby byly czytelne), a po zamknieciu z powrotem na ciemne.
// To pojedynczy, dyskretny flip — nie da sie zmigotac.
export const useStatusBarDim = () => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!FLIP_STATUS_BAR_ICONS) return; // wariant bez zmiany ikon — sam webowy scrim dimuje paski

    let lightIcons = false;   // czy statusbar ma teraz BIALE ikony
    let clearTimer = 0;
    let raf = 0;

    const setStatusIcons = (light) => {
      if (light === lightIcons) return;
      lightIcons = light;
      SafeArea.setSystemBarsStyle({
        // Dark = jasna (biala) tresc na ciemnym tle; Light = ciemne ikony na jasnym.
        style: light ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
        type: SystemBarsType.StatusBar,
      }).catch(() => {});
    };

    const evaluate = () => {
      raf = 0;
      const topDark = isDarkScrimVisible() && !topCoveredByLightPanel();
      if (topDark) {
        if (clearTimer) { clearTimeout(clearTimer); clearTimer = 0; }
        setStatusIcons(true);
      } else if (!clearTimer) {
        // Debounce powrotu — chroni przed migotaniem ikon przy zamykaniu / pojedynczej pustej klatce.
        clearTimer = setTimeout(() => {
          clearTimer = 0;
          if (!(isDarkScrimVisible() && !topCoveredByLightPanel())) setStatusIcons(false);
        }, 160);
      }
    };

    const schedule = () => { if (!raf) raf = requestAnimationFrame(evaluate); };

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
    window.addEventListener('bottomsheet:open', schedule);
    window.addEventListener('bottomsheet:close', schedule);
    schedule();

    return () => {
      observer.disconnect();
      window.removeEventListener('bottomsheet:open', schedule);
      window.removeEventListener('bottomsheet:close', schedule);
      if (raf) cancelAnimationFrame(raf);
      if (clearTimer) clearTimeout(clearTimer);
      setStatusIcons(false);
    };
  }, []);
};
